import React, {useEffect, useState} from "react";

// Utils
import moment from "moment-timezone";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchAutoCharges,
  fetchAutoChargesSummary,
} from "../../../redux/features/sales/autoChargesSlice";

// Routing
import {useHistory} from "react-router-dom";

// Components
import MaterialTable from "material-table";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Link from "@material-ui/core/Link";
import Button from "@material-ui/core/Button";
import MuiAlert from "@material-ui/lab/Alert";
import {makeStyles} from "@material-ui/core/styles";

// Icons
import tableIcons from "../../Table/TableIcons";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";

const useStyles = makeStyles(theme => ({
  counter: {cursor: "pointer"},
  counterActive: {outline: `2px solid ${theme.palette.primary.main}`},
  value: {fontSize: 28, fontWeight: 600, lineHeight: 1.1},
  label: {color: theme.palette.text.secondary, fontSize: 12},
  problem: {color: theme.palette.error.main},
  header: {marginBottom: theme.spacing(2)},
  error: {whiteSpace: "normal", wordBreak: "break-word"},
}));

// A charge the sweep could not confirm either way needs a human to check
// Stripe before anyone charges again - it is not the same as a decline, and
// must not be treated as one.
const NEEDS_REVIEW_HINT =
  "The sweep could not confirm whether Stripe took this payment. Check Stripe for a charge on this order BEFORE charging again.";

const STATUS_COLORS = {
  succeeded: "primary",
  scheduled: "default",
  processing: "secondary",
  failed: "secondary",
  canceled: "default",
};

const COUNTERS = [
  {key: "failed", label: "Failed", problem: true},
  {key: "needs_review", label: "Needs review", problem: true},
  {key: "processing", label: "Stuck", problem: true},
  {key: "overdue", label: "Overdue", problem: true},
  {key: "scheduled", label: "Scheduled"},
  {key: "succeeded", label: "Collected"},
];

const AutoChargesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const {autoChargesList, summary, isLoading, error} = useSelector(
    state => state.autoCharges
  );

  const [statusFilter, setStatusFilter] = useState("");

  const reload = () => {
    dispatch(fetchAutoCharges(statusFilter));
    dispatch(fetchAutoChargesSummary());
  };

  useEffect(() => {
    dispatch(fetchAutoCharges(statusFilter));
    dispatch(fetchAutoChargesSummary());
  }, [dispatch, statusFilter]);

  const money = value =>
    `$${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2} className={classes.header}>
          {COUNTERS.map(counter => {
            const isFilter =
              counter.key !== "needs_review" && counter.key !== "overdue";
            const active = statusFilter === counter.key;
            return (
              <Grid item key={counter.key}>
                <Card
                  className={`${isFilter ? classes.counter : ""} ${
                    active ? classes.counterActive : ""
                  }`}
                  onClick={() =>
                    isFilter && setStatusFilter(active ? "" : counter.key)
                  }>
                  <CardContent>
                    <Typography
                      className={`${classes.value} ${
                        counter.problem && summary[counter.key]
                          ? classes.problem
                          : ""
                      }`}>
                      {summary[counter.key] || 0}
                    </Typography>
                    <Typography className={classes.label}>
                      {counter.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          <Grid item>
            <Button
              startIcon={<RefreshIcon />}
              onClick={reload}
              style={{marginTop: 24}}>
              Refresh
            </Button>
            {statusFilter && (
              <Button onClick={() => setStatusFilter("")} style={{marginTop: 24}}>
                Clear filter
              </Button>
            )}
          </Grid>
        </Grid>

        {error && (
          <MuiAlert severity="error" style={{marginBottom: 16}}>
            {error.errorMessage || "Could not load the auto-charge schedule."}
          </MuiAlert>
        )}

        <MaterialTable
          icons={tableIcons}
          isLoading={isLoading}
          title={
            statusFilter
              ? `Automatic Charges - ${statusFilter}`
              : "Automatic Charges"
          }
          columns={[
            {
              title: "Order",
              field: "order_id",
              type: "numeric",
              width: 90,
              render: rowData => (
                <Link
                  component="button"
                  onClick={() =>
                    history.push(`/sales/orders/${rowData.order_id}`)
                  }>
                  #{rowData.order_id}
                </Link>
              ),
            },
            {title: "Guest", field: "customer_name"},
            {
              title: "Status",
              field: "status",
              render: rowData => (
                <>
                  <Chip
                    size="small"
                    label={rowData.status}
                    color={STATUS_COLORS[rowData.status] || "default"}
                  />
                  {rowData.needs_review && (
                    <Tooltip title={NEEDS_REVIEW_HINT}>
                      <Chip
                        size="small"
                        label="check stripe"
                        color="secondary"
                        style={{marginLeft: 4}}
                      />
                    </Tooltip>
                  )}
                </>
              ),
            },
            {
              title: "Charge date",
              field: "due_date",
              render: rowData =>
                moment.utc(rowData.due_date).format("MMM DD, YYYY"),
            },
            {
              title: "Overdue",
              field: "days_overdue",
              type: "numeric",
              render: rowData =>
                rowData.status === "scheduled" && rowData.days_overdue > 0 ? (
                  <span className={classes.problem}>
                    {rowData.days_overdue}d
                  </span>
                ) : (
                  "-"
                ),
            },
            {
              title: "Balance",
              field: "balance",
              type: "numeric",
              render: rowData => money(rowData.balance),
            },
            {
              title: "Notified",
              field: "advance_notice_sent_at",
              render: rowData =>
                rowData.advance_notice_sent_at
                  ? moment(rowData.advance_notice_sent_at).format("MMM DD HH:mm")
                  : "-",
            },
            {
              title: "Attempted",
              field: "attempted_at",
              render: rowData =>
                rowData.attempted_at
                  ? moment(rowData.attempted_at).format("MMM DD HH:mm")
                  : "-",
            },
            {
              title: "Error",
              field: "last_error",
              render: rowData =>
                rowData.last_error ? (
                  <Tooltip title={rowData.last_error}>
                    <span className={classes.error}>
                      {rowData.last_error.length > 60
                        ? `${rowData.last_error.slice(0, 60)}...`
                        : rowData.last_error}
                    </span>
                  </Tooltip>
                ) : (
                  "-"
                ),
            },
          ]}
          // Copied, not passed through. Redux Toolkit freezes state and
          // material-table writes a tableData property onto every row, which
          // throws "object is not extensible" on a frozen object. The same
          // copy is why DisplayTablePage builds formattedData.
          data={autoChargesList.map(row => ({...row}))}
          actions={[
            {
              icon: () => <VisibilityIcon />,
              tooltip: "Open order",
              onClick: (event, rowData) =>
                history.push(`/sales/orders/${rowData.order_id}`),
            },
          ]}
          options={{
            search: true,
            actionsColumnIndex: -1,
            pageSize: 10,
            thirdSortClick: false,
            rowStyle: rowData => ({
              backgroundColor:
                rowData.status === "failed" || rowData.status === "processing"
                  ? "rgba(244, 67, 54, 0.08)"
                  : undefined,
            }),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AutoChargesPage;
